import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCaptcha } from '../../api/resources';

// Renders the Security Verification block: an auto-generated code with a
// refresh icon, an input, and a live "expires in Ns" countdown that auto-refreshes.
export default function CaptchaBox({ onCaptcha, error }) {
  const [captcha, setCaptcha] = useState(null);
  const [typed, setTyped] = useState('');
  const [remaining, setRemaining] = useState(0);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const c = await fetchCaptcha();
      setCaptcha(c);
      setRemaining(c.expiresInSeconds || 60);
    } catch {
      setCaptcha(null);
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearInterval(timer.current);
  }, [load]);

  useEffect(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          load();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [captcha, load]);

  // Notify parent whenever captcha or typed changes.
  useEffect(() => {
    if (onCaptcha) onCaptcha({ challengeId: captcha?.challengeId, code: typed });
  }, [captcha, typed, onCaptcha]);

  return (
    <div className="rounded-xl border border-line bg-surface-sunken p-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-content-muted">Security Verification</p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-brand-400 bg-surface px-4 py-2 font-mono text-xl font-bold tracking-[0.3em] text-brand-800">
          {captcha?.code || '······'}
        </div>
        <button
          type="button"
          onClick={load}
          className="rounded-md p-2 text-content-muted hover:bg-surface-hover"
          title="Refresh code"
          aria-label="Refresh captcha"
        >
          ⟳
        </button>
        <div className="text-xs text-content-muted">
          {captcha && (
            <>
              <span className="block font-medium text-danger-500">
                {remaining > 0 ? `CAPTCHA expires in ${remaining}s` : 'Refreshing…'}
              </span>
              <span className="text-content-subtle">expires in {remaining}s</span>
            </>
          )}
        </div>
      </div>
      <input
        className="input mt-3"
        placeholder="Enter the code above"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="mt-1 text-xs font-medium text-danger-600">{error}</p>}
    </div>
  );
}